"use client"

import {
  HotelFeePolicy,
  HotelMarkupContext,
  HotelRoom,
} from "@/lib/redux/features/hotel/type"
import {
  getRoomPerNightPrice,
  getRoomTotalPayment,
} from "@/utils/hotel/getRoomPerNightPrice"
import { useAppSelector } from "@/lib/redux/hooks"
import {
  applyHotelMarkupToAmount,
  getHotelBookingMarkup,
  getHotelDisplayPrice,
  selectHotelMarkupPolicy,
} from "@/utils/hotel/hotelFeeMarkup"
import { useCallback, useMemo } from "react"

type UseHotelMarkupPolicyOptions = {
  feePolicies?: HotelFeePolicy[]
}

const getNights = (checkIn?: string, checkOut?: string) => {
  if (!checkIn || !checkOut) return 1
  const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime()
  const nights = Math.round(diff / (1000 * 60 * 60 * 24))
  return nights > 0 ? nights : 1
}

export const useHotelMarkupPolicy = (options?: UseHotelMarkupPolicyOptions) => {
  const detailPolicies = useAppSelector(
    (state) => state.hotel.hotels_detail.feePolicies,
  )
  const searchPolicies = useAppSelector(
    (state) => state.hotel.hotels_search.feePolicies,
  )
  const bookingPolicies = useAppSelector(
    (state) => (state.booking.hotel as { feePolicies?: HotelFeePolicy[] })?.feePolicies,
  )
  const roomsNum = useAppSelector((state) => state.hotel.formSearch.roomsNum || 1)
  const checkInDate = useAppSelector((state) => state.hotel.formSearch.checkInDate)
  const checkOutDate = useAppSelector((state) => state.hotel.formSearch.checkOutDate)

  const policy = useMemo(() => {
    const merged = [
      ...(options?.feePolicies ?? []),
      ...(detailPolicies ?? []),
      ...(searchPolicies ?? []),
      ...(bookingPolicies ?? []),
    ]
    return selectHotelMarkupPolicy(merged.length ? merged : undefined)
  }, [options?.feePolicies, detailPolicies, bookingPolicies, searchPolicies])

  const defaultContext: HotelMarkupContext = useMemo(
    () => ({
      roomsNum,
      nights: getNights(checkInDate, checkOutDate),
    }),
    [roomsNum, checkInDate, checkOutDate],
  )

  const applyMarkupPerNight = useCallback(
    (basePerNight: number, context: Partial<HotelMarkupContext> = {}) =>
      getHotelDisplayPrice(basePerNight, policy, {
        ...defaultContext,
        ...context,
      }),
    [policy, defaultContext],
  )

  const applyMarkupTotal = useCallback(
    (baseTotal: number, context: Partial<HotelMarkupContext> = {}) =>
      applyHotelMarkupToAmount(baseTotal, policy, {
        ...defaultContext,
        ...context,
      }),
    [policy, defaultContext],
  )

  const getMarkupAmount = useCallback(
    (baseTotal: number, context: Partial<HotelMarkupContext> = {}) =>
      getHotelBookingMarkup(baseTotal, policy, {
        ...defaultContext,
        ...context,
      }),
    [policy, defaultContext],
  )

  const getPerNightPrice = useCallback(
    (room: Pick<HotelRoom, "perRoomPerNightRate">, context: Partial<HotelMarkupContext> = {}) =>
      getRoomPerNightPrice(room, policy, { ...defaultContext, ...context }),
    [policy, defaultContext],
  )

  const getTotalPayment = useCallback(
    (
      room: Pick<HotelRoom, "perRoomPerNightRate" | "totalPayment">,
      context: Partial<HotelMarkupContext> = {},
    ) =>
      getRoomTotalPayment(room, policy, { ...defaultContext, ...context }),
    [policy, defaultContext],
  )

  return {
    policy,
    applyMarkupPerNight,
    applyMarkupTotal,
    getMarkupAmount,
    getPerNightPrice,
    getTotalPayment,
    defaultContext,
  }
}
